import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { isShowBetBuilderLegGroupScore, isShowBetBuilderSlipMainScore } from '@/components/betBuilder/betBuilderOddsDisplay';
import type { IBetBuilderTicket } from '@/interface/betSlip';
import { useSimpleEventSubscriptionCallback } from '@/libraries/oddsApi/composables/useSimpleEventSubscriptionCallback';
import { formatEventResultScores } from '@/libraries/oddsApi/oddsApiFormatter';
import { useBetBuilderBetSlipStore } from '@/stores/betSlip/betBuilderBetSlipStore';
import { useRootStore } from '@/stores/rootStore';

function formatScoreDisplay(homeScore: number, awayScore: number): string {
    return `[${homeScore}:${awayScore}]`;
}

export function useBetBuilderSlipScore() {
    const { betSlip } = storeToRefs(useBetBuilderBetSlipStore());
    const { setMatchEventResults } = useBetBuilderBetSlipStore();
    const { isMixParlayMode } = storeToRefs(useRootStore());

    const matchId = computed(() => betSlip.value.match.matchId);
    const isSubscriptionActive = computed(() => matchId.value > 0);

    const { onSubscription } = useSimpleEventSubscriptionCallback({
        eventId: matchId,
        isMixParlay: isMixParlayMode,
        isActive: isSubscriptionActive,
    });

    // Place Bet reads the same event results from the store, so the subscribed scores must land there
    // instead of staying local to this composable — otherwise the request would keep sending the
    // snapshot taken when the last selection was added.
    onSubscription((event) => {
        setMatchEventResults(event ? formatEventResultScores(event.eventResults) : []);
    });

    const marketTypes = computed(() => betSlip.value.tickets.map(ticket => ticket.marketType));

    const mainScoreDisplay = computed(() => (isShowBetBuilderSlipMainScore(betSlip.value.match.isLive, marketTypes.value)
        ? formatScoreDisplay(betSlip.value.match.liveHomeScore, betSlip.value.match.liveAwayScore)
        : ''));

    function getTicketScoreDisplay(ticket: IBetBuilderTicket): string {
        if (!isShowBetBuilderLegGroupScore(betSlip.value.match.isLive, ticket.marketType)) {
            return '';
        }

        const liveScore = betSlip.value.selectionLiveScores.get(ticket.selection.id);

        return liveScore ? formatScoreDisplay(liveScore.home, liveScore.away) : '';
    }

    return {
        mainScoreDisplay,
        getTicketScoreDisplay,
        onSubscription,
    };
}
